import { Action } from 'redux';

import {
    DataKey,
    determineTrendPeriods,
    findLevel,
    shouldGotoDashboard,
} from './config.insight.model';
import { getPeriodEndDate } from './function/get-period-end-date.function';
import { RowId } from './grid.insight.model';
import {
    ActionType,
    GridExportAction,
    GridResetToggleColumnAction,
    GridRetrieveDataAction,
    GridRetrieveDataCancelAction,
    GridRetrieveDataResultAction,
    GridSortAction,
    GridToggleColumnAction,
    LegendRetrieveDataAction,
    LegendRetrieveDataResultAction,
    PeriodSelectEventDateAction,
    PeriodSelectPeriodTypeAction,
    PeriodSelectPeriodTypeResultAction,
    RootCheckPreviousLevelsRecurseResultAction,
    RootClearSelectedVisualItemAction,
    RootListDefinitionSummaryAction,
    RootListDefinitionSummaryCancelAction,
    RootListDefinitionSummaryResultAction,
    RootOverrideAction,
    RootReloadConfigAction,
    RootReloadConfigResultAction,
    RootRetrieveConfigAction,
    RootRetrieveConfigResultAction,
    RootSelectLevelOptionAction,
    RootSelectLevelOptionResultAction,
    RootSelectMetricsAction,
    RootSelectOptionalColumnsAction,
    RootSelectPathAction,
    RootSelectPillarsAction,
    RootSelectPresentationViewAction,
    RootSelectVisualItemAction,
    RootSelectVisualItemResultAction,
    RootSetTrendHideFirstGridColumnAction,
    TrendRetrieveDataAction,
    TrendRetrieveDataCancelAction,
    TrendRetrieveDataResultAction,
    TrendSelectPeriodTypeAction,
    VisualHoverItemAction,
    VisualRetrieveDataAction,
    VisualRetrieveDataCancelAction,
    VisualRetrieveDataResultAction,
} from './insight.action';
import {
    BaseState,
    defaultBaseState,
    defaultPresentationView,
} from './insight.state';
import {
    OptionalColumn,
    OptionalColumnType,
} from './optional-column.insight.model';
import { PresentationView } from './presentation.insight.model';

export interface ReducerFns<TState, TAction> {
    [key: string]: (state: TState, action: Action<TAction>) => TState;
}

const rootFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY](
        state,
        action: RootListDefinitionSummaryAction,
    ): BaseState {
        return {
            ...state,
            // ...defaultBaseState(),
            pending: {
                ...state.pending,
                summary: true,
            },
            summary: undefined,
        };
    },
    [ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_RESULT](
        state,
        action: RootListDefinitionSummaryResultAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                summary: false,
            },
            summary: action.summary,
            activeCategory: action.category,
            activeSubCategory: action.subCategory,
            activeDefinition: action.definition,
        };
    },
    [ActionType.INSIGHT__ROOT__LIST_DEFINITION_SUMMARY_CANCEL](
        state,
        action: RootListDefinitionSummaryCancelAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                summary: false,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_LEVEL_OPTION](
        state,
        action: RootSelectLevelOptionAction,
    ): BaseState {
        return {
            ...state,
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_LEVEL_OPTION_RESULT](
        state,
        action: RootSelectLevelOptionResultAction,
    ): BaseState {
        return {
            ...state,
            activeCategory: action.category,
            activeSubCategory: action.subCategory,
            activeDefinition: action.definition,
        };
    },
    [ActionType.INSIGHT__ROOT__RETRIEVE_CONFIG](
        state,
        action: RootRetrieveConfigAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                config: true,
            },
            activeConfig: action.identifier,
            configs: {
                ...state.configs,
                [action.identifier]: undefined,
            },
            component: undefined,
            configUserInfo: undefined,
            activeView: undefined,
            viewContext: undefined,
            activePath: undefined,
            initialDataFilterOpts: undefined,
            activeViewPillar: undefined,
            activeViewMetric: undefined,
            activeMetric: undefined,
            activeDataMetric: undefined,
            activePillars: undefined,
            activeMetrics: undefined,
            presentAs: defaultPresentationView,
            period: {
                ...state.period,
                periodType: action.persistPeriod
                    ? state.period.periodType
                    : undefined,
                cacheDates: action.persistPeriod
                    ? state.period.cacheDates
                    : undefined,
                eventDate: action.persistPeriod
                    ? state.period.eventDate
                    : undefined,
                gotoDashboard: action.persistPeriod
                    ? state.period.gotoDashboard
                    : undefined,
            },
            visual: {
                ...state.visual,
                data: undefined,
                peerGroupData: undefined,
            },
            grid: {
                ...state.grid,
                supportedColumns: undefined,
                activeColumns: undefined,
                data: undefined,
                peerGroupData: undefined,
                hierarchyUserInfo: undefined,
                lastToggleColumnState: undefined,
            },
            trend: {
                ...state.trend,
                supportedColumns: undefined,
                activeColumns: undefined,
                periods: undefined,
                periodType: undefined,
                data: undefined,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__RETRIEVE_CONFIG_RESULT](
        state,
        action: RootRetrieveConfigResultAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                config: false,
            },
            configs: {
                ...state.configs,
                [action.identifier]: action.config,
            },
            component: action.component,
            configUserInfo: action.configUserInfo,
            activeView: action.view,
            viewContext: action.viewContext,
            activePath: action.path,
            initialDataFilterOpts: action.initialDataFilterOpts,
            roleHierarchy: action.roleHierarchy,
            cacheLangId: action.cacheLangId,
            optionalColumns: action.optionalColumns,
            period: {
                ...state.period,
                periodType: action.periodType,
                cacheDates: action.cacheDates,
                eventDate: action.eventDate,
                gotoDashboard: action.gotoDashboard,
            },
            trend: {
                ...state.trend,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                periods:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    action.trendPeriods &&
                    action.trendPeriods.map((e) => e.periodType),
                startDates: action.trendPeriods,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__RELOAD_CONFIG](
        state,
        action: RootReloadConfigAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                configReload: true,
            },
            component: undefined,
            configUserInfo: undefined,
            activeView: undefined,
            viewContext: undefined,
            activePath: undefined,
            initialDataFilterOpts: undefined,
            activeViewPillar: undefined,
            activeViewMetric: undefined,
            activeMetric: undefined,
            activeDataMetric: undefined,
            activePillars: undefined,
            activeMetrics: undefined,
            presentAs: defaultPresentationView,
            visual: {
                ...state.visual,
                data: undefined,
                peerGroupData: undefined,
            },
            grid: {
                ...state.grid,
                supportedColumns: undefined,
                activeColumns: undefined,
                data: undefined,
                peerGroupData: undefined,
                hierarchyUserInfo: undefined,
                lastToggleColumnState: undefined,
            },
            trend: {
                ...state.trend,
                supportedColumns: undefined,
                activeColumns: undefined,
                data: undefined,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__RELOAD_CONFIG_RESULT](
        state,
        action: RootReloadConfigResultAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                configReload: false,
            },
            configs: {
                ...state.configs,
                [action.identifier]: action.config, // TODO revise. just swap out views and paths?
            },
            component: action.component,
            configUserInfo: action.configUserInfo,
            activeView: action.view,
            viewContext: action.viewContext,
            activePath: action.path,
            initialDataFilterOpts: action.initialDataFilterOpts,
            cacheLangId: action.cacheLangId,
            optionalColumns: action.optionalColumns,
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_VISUAL_ITEM](
        state,
        action: RootSelectVisualItemAction,
    ): BaseState {
        return {
            ...state,
            pending: {
                ...state.pending,
                supportedColumns: true,
            },
            activeViewPillar: action.viewPillar,
            activeViewMetric: action.viewMetric,
            activeMetric: action.metric,
            activeDataMetric: action.dataMetric,
            activePillars: undefined,
            activeMetrics: undefined,
            presentAs: defaultPresentationView,
            grid: {
                ...state.grid,
                supportedColumns: undefined,
                activeColumns: undefined,
                data: undefined,
                peerGroupData: undefined,
                hierarchyUserInfo: undefined,
                hierarchyUserInfoCurrent: undefined,
            },
            trend: {
                ...state.trend,
                supportedColumns: undefined,
                activeColumns: undefined,
                periodType:
                    !state.trend.periods || state.trend.periods.length === 0
                        ? undefined
                        : state.trend.periods[0],
                data: undefined,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_VISUAL_ITEM_RESULT](
        state,
        action: RootSelectVisualItemResultAction,
    ): BaseState {
        const gridSupportedColumns: OptionalColumn[] = JSON.parse(
            JSON.stringify(action.columns),
        );
        gridSupportedColumns.forEach((e) => {
            // TODO revise. current actual always enabled
            e.forced = e.id === OptionalColumnType.CURRENT_ACTUAL;
            e.disabled = e.id === OptionalColumnType.CURRENT_ACTUAL;
        });
        const gridActiveColumns = gridSupportedColumns.filter((e) =>
            action.columns.some((ee) => ee.id === e.id),
        );

        const trendSupportedColumnTypes = [
            OptionalColumnType.CURRENT_ACTUAL,
            OptionalColumnType.PROJECTED_RESULT,
            OptionalColumnType.PERIOD_TARGET,
            OptionalColumnType.GAP,
            OptionalColumnType.PROJECTED_PERCENT_TO_TARGET,
            OptionalColumnType.PREVIOUS_ACTUAL,
            OptionalColumnType.CURRENT_TARGET,
            OptionalColumnType.GOAL,
            OptionalColumnType.PERCENT_TO_TARGET,
            OptionalColumnType.PEER_GROUP_ACTUAL,
            OptionalColumnType.PEER_GROUP_PERCENT_TO_TARGET,
        ];
        const trendSupportedColumns: OptionalColumn[] = JSON.parse(
            JSON.stringify(action.columns),
        );
        // eslint-disable-next-line no-return-assign, @typescript-eslint/prefer-includes
        trendSupportedColumns.forEach(
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            (e) => (e.hidden = trendSupportedColumnTypes.indexOf(e.id) === -1),
        );
        const trendActiveColumns = trendSupportedColumns.filter(
            (e) =>
                !e.hidden &&
                action.columns.some((ee) => ee.id === e.id) &&
                e.id !== OptionalColumnType.PREVIOUS_ACTUAL,
        );

        return {
            ...state,
            pending: {
                ...state.pending,
                supportedColumns: false,
            },
            // activePath: action.path,
            activePillars: action.pillars,
            activeMetrics: action.metrics,
            grid: {
                ...state.grid,
                supportedColumns: gridSupportedColumns,
                activeColumns: gridActiveColumns,
            },
            trend: {
                ...state.trend,
                supportedColumns: trendSupportedColumns,
                activeColumns: trendActiveColumns,
            },
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_PRESENTATION_VIEW](
        state,
        action: RootSelectPresentationViewAction,
    ): BaseState {
        return {
            ...state,
            presentAs: action.presentationView,
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_PATH](
        state,
        action: RootSelectPathAction,
    ): BaseState {
        return {
            ...state,
            activePath: action.path,
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_PILLARS](
        state,
        action: RootSelectPillarsAction,
    ): BaseState {
        return {
            ...state,
            activePillars: action.pillars,
        };
    },
    [ActionType.INSIGHT__ROOT__SELECT_OPTIONAL_COLUMNS](
        state: BaseState,
        action: RootSelectOptionalColumnsAction,
    ): BaseState {
        const next = {
            ...state,
        };
        if (state.presentAs === PresentationView.GRID) {
            next.grid = {
                ...state.grid,
                activeColumns: action.columns,
            };
        }
        if (state.presentAs === PresentationView.TREND) {
            next.trend = {
                ...state.trend,
                activeColumns: action.columns,
            };
        }
        return next;
    },
    [ActionType.INSIGHT__ROOT__SELECT_METRICS](
        state,
        action: RootSelectMetricsAction,
    ): BaseState {
        return {
            ...state,
            activeMetrics: action.metrics,
        };
    },
    [ActionType.INSIGHT__ROOT__SET_TREND_HIDE_FIRST_GRID_COLUMN](
        state,
        action: RootSetTrendHideFirstGridColumnAction,
    ): BaseState {
        return {
            ...state,
            trendHideFirstGridColumn: action.trendHideFirstGridColumn,
        };
    },
    [ActionType.INSIGHT__ROOT__CLEAR_SELECTED_VISUAL_ITEM](
        state,
        action: RootClearSelectedVisualItemAction,
    ): BaseState {
        return {
            ...state,
            activeViewPillar: undefined,
            activeViewMetric: undefined,
            activeMetric: undefined,
            activeDataMetric: undefined,
        };
    },
    [ActionType.INSIGHT__ROOT__CHECK_PREVIOUS_LEVELS_RECURSE_RESULT](
        state,
        action: RootCheckPreviousLevelsRecurseResultAction,
    ): BaseState {
        const level = findLevel(state.activePath.levels, action.identifier);
        if (level) {
            for (let i = 0; i < level.levels.length; i++) {
                level.levels[i] = action.levels[i];
            }
        }
        return {
            ...state,
        };
    },
    [ActionType.INSIGHT__ROOT__OVERRIDE](
        state,
        action: RootOverrideAction,
    ): BaseState {
        return {
            ...state,
            override: action.override || state.override,
            activeView: action.view || state.activeView,
            defaultPath: action.defaultPath || state.defaultPath,
            activePath: action.path || state.activePath,
            initialDataFilterOpts:
                action.initialDataFilterOpts || state.initialDataFilterOpts,
        };
    },
};

const periodFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__PERIOD__SELECT_PERIOD_TYPE](
        state,
        action: PeriodSelectPeriodTypeAction,
    ): BaseState {
        return {
            ...state,
            period: {
                ...state.period,
                pending: {
                    ...state.period.pending,
                    periodType: true,
                },
                periodType: action.periodType,
                cacheDates: undefined,
                eventDate: undefined,
                gotoDashboard: undefined,
            },
            trend: {
                ...state.trend,
                periods: undefined,
                periodType: undefined,
            },
        };
    },
    [ActionType.INSIGHT__PERIOD__SELECT_PERIOD_TYPE_RESULT](
        state,
        action: PeriodSelectPeriodTypeResultAction,
    ): BaseState {
        return {
            ...state,
            period: {
                ...state.period,
                pending: {
                    ...state.period.pending,
                    periodType: false,
                },
                periodType: action.periodType,
                cacheDates: action.cacheDates,
                eventDate: action.eventDate,
                gotoDashboard: action.gotoDashboard,
            },
            trend: {
                ...state.trend,
                periods: action.trendPeriods.map((e) => e.periodType),
                startDates: action.trendPeriods,
            },
        };
    },
    [ActionType.INSIGHT__PERIOD__SELECT_EVENT_DATE](
        state,
        action: PeriodSelectEventDateAction,
    ): BaseState {
        const trendPeriods = determineTrendPeriods(
            action.eventDate,
            state.period.cacheDates,
        );
        return {
            ...state,
            period: {
                ...state.period,
                eventDate: action.eventDate,
                gotoDashboard: shouldGotoDashboard(
                    state.configs[state.activeConfig].dashboardDateRanges,
                    action.eventDate,
                ),
            },
            trend: {
                ...state.trend,
                periods: trendPeriods.map((e) => e.periodType),
                startDates: trendPeriods,
            },
        };
    },
};

const visualFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__VISUAL__RETRIEVE_DATA](
        state,
        action: VisualRetrieveDataAction,
    ): BaseState {
        return {
            ...state,
            visual: {
                ...state.visual,
                pending: {
                    ...state.visual.pending,
                    data: true,
                },
                data: undefined,
                peerGroupData: undefined,
            },
        };
    },
    [ActionType.INSIGHT__VISUAL__RETRIEVE_DATA_RESULT](
        state,
        action: VisualRetrieveDataResultAction,
    ): BaseState {
        return {
            ...state,
            visual: {
                ...state.visual,
                pending: {
                    ...state.visual.pending,
                    data: false,
                },
                data: action.data,
                peerGroupData: action.peerGroupData,
            },
        };
    },
    [ActionType.INSIGHT__VISUAL__RETRIEVE_DATA_CANCEL](
        state,
        action: VisualRetrieveDataCancelAction,
    ): BaseState {
        return {
            ...state,
            visual: {
                ...state.visual,
                pending: {
                    ...state.visual.pending,
                    data: false,
                },
            },
        };
    },
    [ActionType.INSIGHT__VISUAL__HOVER_ITEM](
        state,
        action: VisualHoverItemAction,
    ): BaseState {
        return {
            ...state,
            visual: {
                ...state.visual,
                lastHoverItem: action.item,
            },
        };
    },
};

const gridFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__GRID__RETRIEVE_DATA](
        state,
        action: GridRetrieveDataAction,
    ): BaseState {
        const depth = RowId.parse(action.rowId).depth;
        return {
            ...state,
            grid: {
                ...state.grid,
                pending: {
                    ...state.grid.pending,
                    data: {
                        ...state.grid.pending.data,
                        [action.rowId]: true,
                    },
                },
                // tree, so strip data before
                data: !state.grid.data
                    ? undefined
                    : state.grid.data.filter((e) => e.depth <= depth),
                peerGroupData: !state.grid.peerGroupData
                    ? undefined
                    : state.grid.peerGroupData.filter((e) => e.depth <= depth),
            },
        };
    },
    [ActionType.INSIGHT__GRID__RETRIEVE_DATA_RESULT](
        state,
        action: GridRetrieveDataResultAction,
    ): BaseState {
        return {
            ...state,
            grid: {
                ...state.grid,
                pending: {
                    ...state.grid.pending,
                    data: {
                        ...state.grid.pending.data,
                        [action.rowId]: false,
                    },
                },
                data: [...(state.grid.data || []), ...action.data],
                peerGroupData: [
                    ...(state.grid.peerGroupData || []),
                    ...action.peerGroupData,
                ],
                hierarchyUserInfo: [
                    ...(state.grid.hierarchyUserInfo || []),
                    ...action.hierarchyUserInfo,
                ],
                hierarchyUserInfoCurrent: [
                    ...(state.grid.hierarchyUserInfoCurrent || []),
                    ...action.hierarchyUserInfoCurrent,
                ],
            },
        };
    },
    [ActionType.INSIGHT__GRID__RETRIEVE_DATA_CANCEL](
        state,
        action: GridRetrieveDataCancelAction,
    ): BaseState {
        let pending = {};
        if (action.rowIds) {
            pending = {
                ...state.grid.pending.data,
            };
            // eslint-disable-next-line no-return-assign
            action.rowIds.forEach((e) => (pending[e] = false));
        }
        return {
            ...state,
            grid: {
                ...state.grid,
                pending: {
                    ...state.grid.pending,
                    data: pending,
                },
            },
        };
    },
    [ActionType.INSIGHT__GRID__TOGGLE_COLUMN](
        state,
        action: GridToggleColumnAction,
    ): BaseState {
        return {
            ...state,
            grid: {
                ...state.grid,
                lastToggleColumnState: {
                    columnId: action.columnId,
                    pillarExpanded: action.pillarExpanded,
                    metricExpanded: action.metricExpanded,
                },
            },
        };
    },
    [ActionType.INSIGHT__GRID__RESET_TOGGLE_COLUMN](
        state,
        action: GridResetToggleColumnAction,
    ): BaseState {
        return {
            ...state,
            grid: {
                ...state.grid,
                lastToggleColumnState: undefined,
            },
        };
    },
    [ActionType.INSIGHT__GRID__SORT](state, action: GridSortAction): BaseState {
        return {
            ...state,
            grid: {
                ...state.grid,
                sort: {
                    rowIds: action.rowIds,
                    def: action.def,
                },
            },
        };
    },
    [ActionType.INSIGHT__GRID__EXPORT](
        state: BaseState,
        action: GridExportAction,
    ): BaseState {
        return {
            ...state,
            grid: {
                ...state.grid,
                export: new Date(),
            },
        };
    },
};

const trendFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__TREND__SELECT_PERIOD_TYPE](
        state,
        action: TrendSelectPeriodTypeAction,
    ): BaseState {
        return {
            ...state,
            trend: {
                ...state.trend,
                periodType: action.periodType,
                data: undefined,
            },
        };
    },
    [ActionType.INSIGHT__TREND__RETRIEVE_DATA](
        state,
        action: TrendRetrieveDataAction,
    ): BaseState {
        const { periodType, date } = action.opts;

        action.opts.date = getPeriodEndDate(periodType, date);
        return {
            ...state,
            trend: {
                ...state.trend,
                pending: {
                    ...state.trend.pending,
                    data: {
                        ...state.trend.pending.data,
                        [action.rowId]: true,
                    },
                },
            },
        };
    },
    [ActionType.INSIGHT__TREND__RETRIEVE_DATA_RESULT](
        state,
        action: TrendRetrieveDataResultAction,
    ): BaseState {
        return {
            ...state,
            trend: {
                ...state.trend,
                pending: {
                    ...state.trend.pending,
                    data: {
                        ...state.trend.pending.data,
                        [action.rowId]: false,
                    },
                },
                data: [...(state.trend.data || []), ...action.data],
                peerGroupData: [
                    ...(state.trend.peerGroupData || []),
                    ...action.peerGroupData,
                ],
            },
        };
    },
    [ActionType.INSIGHT__TREND__RETRIEVE_DATA_CANCEL](
        state: BaseState,
        action: TrendRetrieveDataCancelAction,
    ): BaseState {
        let pending = {};
        let data = [];
        let peerGroupData = [];
        if (action.rowIds) {
            pending = {
                ...state.trend.pending.data,
            };
            // eslint-disable-next-line no-return-assign
            action.rowIds.forEach((e) => (pending[e] = false));
            // flat, so strip data after
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            const clean = (dat: DataKey[]) => {
                return (dat || ([] as DataKey[])).filter((e) => {
                    return !action.rowIds.some((ee) => {
                        const rid = RowId.parse(ee);
                        return (
                            rid.levelIdentifier === e.levelId &&
                            rid.levelIdentifierPath === e.levelIdPath &&
                            e.data.some((eee) => eee.dataId === rid.dataId)
                        );
                    });
                });
            };
            data = clean(state.trend.data);
            peerGroupData = clean(state.trend.peerGroupData);
        }
        return {
            ...state,
            trend: {
                ...state.trend,
                pending: {
                    ...state.trend.pending,
                    data: pending,
                },
                data,
                peerGroupData,
            },
        };
    },
};

const legendFns: ReducerFns<BaseState, ActionType> = {
    [ActionType.INSIGHT__LEGEND__RETRIEVE_DATA](
        state,
        action: LegendRetrieveDataAction,
    ): BaseState {
        return {
            ...state,
            legend: {
                ...state.legend,
                metricThresholds: undefined,
                trendsArrows: undefined,
            },
        };
    },
    [ActionType.INSIGHT__LEGEND__RETRIEVE_DATA_RESULT](
        state,
        action: LegendRetrieveDataResultAction,
    ): BaseState {
        return {
            ...state,
            legend: {
                ...state.legend,
                metricThresholds: action.metricThresholds,
                trendsArrows: action.trendsArrows,
            },
        };
    },
};

const reducerFns: ReducerFns<any, any> = {
    ...rootFns,
    ...periodFns,
    ...visualFns,
    ...gridFns,
    ...trendFns,
    ...legendFns,
};

const insight = (
    state: BaseState = defaultBaseState(),
    action: Action<ActionType>,
): BaseState => {
    const fn = reducerFns[action.type];
    if (fn) {
        return fn(state, action);
    }
    return state;
};

export default {
    insight,
};
